import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectSetting, settingWriteActions } from './selectSetting';
import type { SettingKey, SettingValue } from './settingsRegistry';
import type { SettingContent, SettingContext, SettingsState } from './settingTypes';

/**
 * A setting's value through the cascade, and a setter that writes the player's
 * change to the right layers. `context` names the mode, the piece, exercise or
 * session, and (for a screen setting) the kind of device; `content` is the
 * content's authored value and any lock on it. While a lock holds, the setter
 * does nothing.
 */
export function useSetting<K extends SettingKey>(
  key: K,
  context: SettingContext = {},
  content: SettingContent<SettingValue<K>> = {},
): [SettingValue<K>, (value: SettingValue<K>) => void] {
  const dispatch = useDispatch();
  const { mode, instanceId, deviceType } = context;
  const { authored, lock } = content;
  // Shallow equality: a record value (the hands) is parsed into a new object on every read.
  const value = useSelector(
    (state: SettingsState) =>
      selectSetting(state, key, { mode, instanceId, deviceType }, { authored, lock }),
    shallowEqual,
  );
  const setValue = React.useCallback(
    (next: SettingValue<K>) => {
      settingWriteActions(key, next, { mode, instanceId, deviceType }, { lock }).forEach((action) =>
        dispatch(action),
      );
    },
    [dispatch, key, mode, instanceId, deviceType, lock],
  );
  return [value, setValue];
}
